import React, { memo } from 'react';
import {createStyles, makeStyles, Theme, useTheme} from "@material-ui/core/styles";
import {useDispatch, useSelector} from 'react-redux';
import {List, ListItem, Typography} from '@material-ui/core';
import { DynamicScrollbar } from '../components';
import {settingsDrawerWidth} from "../settings";
import {
  selectAllFolders
} from '../redux/folders-slice';
import {
  getCameraListFromAssets,
  selectAllAssets,
  setVisibleFolder
} from '../redux/assets-slice';
import { headerHeight } from '../app';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import LocalScene, { SceneCoordinate } from '../viewer/js/projections';
import {
  changeCameraState,
  initialSceneState,
  SceneCameraState
} from '../redux/camera-slice';
import { getLocalizedFolderName } from '../utilities';
import { useViewer } from '../hooks';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    parent: {
      position: "absolute",
      right: theme.spacing(1),
      pointerEvents: "none",
      marginTop: `${headerHeight/2}px`,
      display: "flex",
      height: "100%",
      alignItems: "center"
    },
    offset: {
      right: `calc(${settingsDrawerWidth}px + ${theme.spacing(1)}px)`
    },
    list: {
      zoom: 0.85,
      background: "white",
      borderRadius: theme.spacing(0.5),
      borderRight: `${theme.spacing(1)}px solid ${theme.palette.primary.main}`,
      pointerEvents: "auto",
      boxShadow: "0px 0px 5px 2px rgb(0 0 0 / 25%)"
    },
    listItem: {
      padding: theme.spacing(1, 1.5)
    },
    title: {
      textAlign: "center",
      padding: theme.spacing(1, 1.5),
      fontWeight: "bold",
      zoom: 0.95
    }
  })
);

export const QuickNav = memo((props: any) => {
  const {settingsDrawerState, openFromSettings} = props;

  const dispatch = useDispatch();
  const theme = useTheme();
  const classes = useStyles(theme);
  const {viewer} = useViewer();
  const {t} = useTranslation();

  const assets = useSelector(selectAllAssets);
  const folders = useSelector(selectAllFolders);

  const getFolderAssets = (folderID) => {
    const folderAssets = assets.filter(asset => (asset.folderID === folderID));
    return folderAssets.map(folderAsset => folderAsset.id);
  };

  const camerasForFolder = (folderID) => {
    const cameraData = getCameraListFromAssets(assets, false);
    return cameraData.filter(camera => camera.folder === folderID);
  };

  const getBestSceneState = (cameras) : SceneCameraState => {
    const position = viewer.camera.position;
    let lookat = new SceneCoordinate(position)
      .toDataProjection();

    let data = cameras.map(data => {
      const dx = data.x - lookat.x;
      const dy = data.y - lookat.y;
      const distance = Math.sqrt(dx*dx + dy*dy);
      return {...data, distance};
    }).sort((a,b) => a.distance - b.distance);

    return {
      ...initialSceneState,
      camera: data[0].id,
      lookat: [lookat.x, lookat.y, data[0].z]
    } as SceneCameraState;
  };

  const onClick = (folderID) => {
    if (!viewer) return;

    const cameras = camerasForFolder(folderID);
    const hasCameras = cameras.length > 0;

    if (hasCameras) {
      if (LocalScene.initialized) {
        const sceneState = getBestSceneState(cameras);
        viewer.setInitialStateFlag(false);
        dispatch(changeCameraState(sceneState));
      }
    } else {
      const folderAssetIds = getFolderAssets(folderID);
      viewer.snapToFolder(folderAssetIds);
    }

    dispatch(setVisibleFolder(folderID));
  };

  const isSelected = (folderID) => {
    const folderAssets = assets.filter(x => x.folderID === folderID);
    const numFolderAssets = folderAssets.length;
    const numVisible = folderAssets.filter(x => x.visible).length;
    const numAssetsVisible = assets.filter(x => x.visible).length;

    return (numFolderAssets === numVisible)
      && (numVisible === numAssetsVisible);
  };

  // Only folders with assets inside are visible
  const folderOptions = folders.filter(folder => {
    const items = assets.filter(x => x.folderID === folder.id);
    return items.length > 0;
  });

  const openNav = (folderOptions.length > 1 && openFromSettings);

  return (
    <React.Fragment>
      {openNav && (
        <div className={clsx(classes.parent, {
          [classes.offset]: settingsDrawerState
        })}>
          <div className={classes.list}>
            <DynamicScrollbar
              scrollHeight={250}
              scrollPercent={50}
            >
              <Typography className={classes.title}>
                {t("quick-nav.title")}
              </Typography>

              <List disablePadding>
                {folderOptions.map((folder, index) => {
                  const localizedFolderName = getLocalizedFolderName(folder.name);

                  return (
                    <ListItem
                      key={index}
                      dense
                      button
                      className={classes.listItem}
                      onClick={() => onClick(folder.id)}
                      selected={isSelected(folder.id)}
                    >
                      <Typography noWrap>{localizedFolderName}</Typography>
                    </ListItem>
                  );
                })}
              </List>
            </DynamicScrollbar>
          </div>
        </div>
      )}
    </React.Fragment>
  );
});